import { skipToken } from "@reduxjs/toolkit/query";
import type {
  AccountGradingRecording,
  GradingId,
} from "@repo/app-types/entities/teammatePerformanceSummary";
import type { ToolUsageAccuracy } from "@repo/app-types/entities/toolUsageAccuracy";
import { type FC, useEffect, useMemo, useRef, useState } from "react";
import { usePlaybookGradingCallOutlineQuery } from "../../api/entities/performancePlaybook.js";
import { useRecordingToolActivitiesQuery } from "../../api/entities/recording.js";
import { AudioPlayer } from "../../components/audioPlayer/audioPlayer.js";
import { AudioPlayerProvider } from "../../components/audioPlayer/provider.js";
import { TranscriptScrollPane } from "../../components/transcript/transcriptScrollPane.js";
import { CallNavigatorDropdown } from "../teammatePerformance/callNavigatorDropdown.js";
import { useSortedRecordings } from "../teammatePerformance/gradingHelpers.js";
import { GradingTranscriptWithIndex } from "../teammatePerformance/gradingTranscriptWithIndex.js";
import { ToolUsageAccuracyStatusIcon } from "../teammatePerformance/toolUsageAccuracyStatusIcon.js";
import { TranscriptVideoPanel } from "../teammatePerformance/transcriptVideoPanel.js";
import { useTranscriptOutlineSync } from "../teammatePerformance/useTranscriptOutlineSync.js";

export interface ToolUsageAccuracyCallSlideoverDialogContentProps {
  gradingId: GradingId;
  toolId: string;
  toolName: string;
  recordings: readonly AccountGradingRecording[];
  toolAccuracy: ToolUsageAccuracy | undefined;
}

export const ToolUsageAccuracyCallSlideoverDialogContent: FC<
  ToolUsageAccuracyCallSlideoverDialogContentProps
> = (props) => {
  const { gradingId, toolAccuracy, toolId, toolName } = props;
  const { recordings } = props;
  const sortedRecordings = useSortedRecordings(recordings);
  const hasTranscript = recordings.some((r) => r.transcript);

  const transcriptContainerRef = useRef<HTMLDivElement>(null);
  const { callPosition, scrollToCallPosition } = useTranscriptOutlineSync({
    transcriptContainerRef,
    sortedRecordings,
  });

  // Fetch call outline for overlay navigation
  const {
    data: callOutline,
    isLoading: isCallOutlineLoading,
    isError: isCallOutlineError,
  } = usePlaybookGradingCallOutlineQuery({ gradingId });

  // Fetch tool activities to find this tool's activity timestamp
  const recordingIds = recordings.map((recording) => recording.id);
  const { data: toolActivitiesByRecordingId } = useRecordingToolActivitiesQuery(
    recordingIds.length > 0 ? recordingIds : skipToken,
  );

  // Find the first tool activity for this tool across all recordings
  const firstToolActivity = useMemo(() => {
    if (!toolActivitiesByRecordingId) {
      return undefined;
    }

    for (const recording of sortedRecordings) {
      const activities = toolActivitiesByRecordingId[recording.id];
      if (!activities) {
        continue;
      }

      const activity = activities.find((a) => a.tool_id === toolId);
      if (activity) {
        const recordingStartTime = new Date(recording.recorded_at).getTime();
        const activityTime = new Date(activity.created_at).getTime();
        const timestampInRecording = activityTime - recordingStartTime;

        return {
          recordingId: recording.id,
          timestamp: timestampInRecording,
        };
      }
    }

    return undefined;
  }, [toolActivitiesByRecordingId, sortedRecordings, toolId]);

  const [hasAutoScrolled, setHasAutoScrolled] = useState(false);

  // Auto-scroll to the first tool activity when the component loads
  useEffect(() => {
    if (
      !hasAutoScrolled &&
      firstToolActivity &&
      scrollToCallPosition &&
      transcriptContainerRef.current
    ) {
      // Small delay to ensure the transcript has rendered
      const timeoutId = setTimeout(() => {
        scrollToCallPosition({
          recordingId: firstToolActivity.recordingId,
          timestamp: firstToolActivity.timestamp,
        });
        setHasAutoScrolled(true);
      }, 100);

      return () => clearTimeout(timeoutId);
    }
  }, [hasAutoScrolled, firstToolActivity, scrollToCallPosition]);

  return (
    <AudioPlayerProvider>
      <div className="grid h-full grid-cols-[minmax(0,1fr)_minmax(0,1fr)] grid-rows-[minmax(0,1fr)_auto]">
        {/* Left pane - Tool accuracy details */}
        <div className="relative space-y-6 overflow-y-auto border-r border-border-light p-4">
          {/* Tool accuracy summary */}
          <div className="rounded-md border border-border-light bg-white p-4">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                {toolAccuracy ? (
                  <ToolUsageAccuracyStatusIcon status={toolAccuracy.status} />
                ) : null}
                <div className="text-sm font-medium">{toolName}</div>
              </div>
              {toolAccuracy?.summary ? (
                <div className="text-sm text-gray-600">
                  {toolAccuracy.summary}
                </div>
              ) : (
                <div className="text-sm text-muted-foreground">
                  No summary available.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right pane - Transcript */}
        <div className="flex min-h-0 flex-col bg-white">
          <TranscriptVideoPanel recordings={sortedRecordings} />
          <div className="p-4">
            <CallNavigatorDropdown
              showCurrentKeyMoment
              recordings={sortedRecordings}
              callOutline={callOutline}
              isLoading={isCallOutlineLoading}
              isError={isCallOutlineError}
              callPosition={callPosition}
              onCallPositionRequest={scrollToCallPosition}
            />
          </div>
          <TranscriptScrollPane
            scrollContainerRef={transcriptContainerRef}
            className="px-4"
          >
            <div className="pb-4">
              {hasTranscript ? (
                <GradingTranscriptWithIndex
                  recordings={sortedRecordings}
                  scrollToCallPosition={scrollToCallPosition}
                  toolActivitiesByRecordingId={toolActivitiesByRecordingId}
                />
              ) : (
                <p className="text-sm text-muted-foreground">
                  No transcript available
                </p>
              )}
            </div>
          </TranscriptScrollPane>
        </div>

        <div className="col-span-full">
          <AudioPlayer />
        </div>
      </div>
    </AudioPlayerProvider>
  );
};
